import Phaser from 'phaser';
import { authBypassed } from './config/env';
import {
  initAuthGate,
  presentAuthLoading,
  presentLoggedInWelcome,
  presentLoginForm,
  resolveAuthIfLoggedIn,
  showAuthShell,
  showAuthStatus,
  waitForAuth,
  waitForWelcomeBackReady,
  ensureAuthBootstrap,
} from './auth/auth-gate';
import { hasSession, getLastBackendLogin } from './net/session';
import { tryTelegramLogin, isTelegramMiniApp } from './auth/telegram-auth';
import { initNativeOAuth, initAppLifecycle } from './auth/native-oauth';
import {
  consumeOAuthReturnFlag,
  isOAuthRedirectReturn,
  navigateToAppRootAfterOAuth,
} from './auth/gaupa/login';
import {
  hideSplashBoot,
  initSplashScreen,
  notifyAssetsLoadComplete,
  resetSplashForRelogin,
  skipSplashLoginGate,
  waitForSplashLoginMenu,
} from './auth/splash-screen';
import {
  isBackendEnabled,
  onAppBackground,
  onAppForeground,
  stopEnergyRegenTicker,
  stopProfilePoll,
  syncFromServer,
} from './net/backend';

let gameInstance: Phaser.Game | null = null;

export function getGameInstance() {
  return gameInstance;
}

export function destroyGameInstance() {
  if (!gameInstance) return;
  stopProfilePoll();
  stopEnergyRegenTicker();
  gameInstance.destroy(true);
  gameInstance = null;
}

export async function runAuthGate(opts?: { skipSplash?: boolean }) {
  await initNativeOAuth();

  const oauthReturn = isOAuthRedirectReturn();
  const skipSplash = opts?.skipSplash === true || oauthReturn;
  if (oauthReturn) consumeOAuthReturnFlag();

  if (!authBypassed()) {
    if (oauthReturn) {
      // OAuth redirect: loading only → welcome / welcome-back (never the login form).
      hideSplashBoot();
      showAuthShell({ showLogin: false });
      initAuthGate();
      presentAuthLoading('Completing sign-in…');
      await ensureAuthBootstrap();
      if (hasSession()) {
        navigateToAppRootAfterOAuth();
        presentLoggedInWelcome();
        resolveAuthIfLoggedIn();
      } else {
        presentLoginForm('Sign-in failed — try again.');
      }
    } else if (skipSplash) {
      // Session expired / re-auth: login form only, no cinematic.
      hideSplashBoot();
      showAuthShell({ showLogin: false });
      initAuthGate();
      if (!hasSession()) {
        presentLoginForm();
      } else {
        presentLoggedInWelcome();
        resolveAuthIfLoggedIn();
      }
    } else {
      showAuthShell({ showLogin: false });
      initAuthGate();
      initSplashScreen();
      notifyAssetsLoadComplete();

      try {
        const login = await tryTelegramLogin();
        if (login) {
          skipSplashLoginGate();
          presentLoggedInWelcome(login as Parameters<typeof presentLoggedInWelcome>[0]);
          resolveAuthIfLoggedIn();
        }
      } catch (err) {
        console.warn('[auth] Telegram login failed:', err);
        showAuthStatus('Telegram sign-in failed — use email or OAuth below.');
      }

      await ensureAuthBootstrap();

      if (hasSession() && isTelegramMiniApp() && !getLastBackendLogin()) {
        skipSplashLoginGate();
        presentLoggedInWelcome();
        resolveAuthIfLoggedIn();
      }

      // Cinematic → stone login overlay (or skipped when Telegram already signed in)
      await waitForSplashLoginMenu();
    }

    await waitForAuth();
    await waitForWelcomeBackReady();
  } else if (!skipSplash) {
    // Dev / skip-auth: still play the branded cinematic, then enter game.
    showAuthShell({ showLogin: false });
    initSplashScreen();
    notifyAssetsLoadComplete();
    await waitForSplashLoginMenu();
  }

  hideSplashBoot();
  // Keep the auth/welcome shell up until the game loading overlay is showing,
  // then Phaser boots under the loader so UI never flickers in piece by piece.
  presentAuthLoading('Loading your adventure…');

  if (isBackendEnabled()) {
    await syncFromServer();
  }

  void initAppLifecycle(onAppBackground, onAppForeground);
}

export function setGameInstance(game: Phaser.Game) {
  gameInstance = game;
}

export function handleSessionExpired() {
  destroyGameInstance();
  resetSplashForRelogin();
  showAuthShell({ showLogin: false });
  initAuthGate();
  presentLoginForm('Session expired — please sign in again.');
  window.dispatchEvent(new CustomEvent('booga:restart-auth'));
}
